import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  ExternalLink,
  FileDown,
  FileCheck,
  Loader2,
  Pencil,
  Settings,
  Sparkles,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import { OnboardingBrandMark } from "@/components/onboarding/OnboardingBrandMark";
import { OnboardingStepTag } from "@/components/onboarding/OnboardingStepTag";
import { OnboardingBrandColumn } from "@/components/onboarding/OnboardingBrandColumn";
import { getDgiConfigTrail } from "@/components/onboarding/OnboardingTrail";
import type { SituationChoice } from "@/pages/onboarding/OnboardingSituationStep";
import { OnboardingChoiceCard } from "@/components/onboarding/OnboardingChoiceCard";
import { OnboardingAutoFlowPanel } from "@/components/onboarding/OnboardingAutoFlowPanel";
import type { CertificateRequestCompanyType } from "@/pages/certificate-requests/idDigitalUrls";
import type { CreateCertificateRequestPayload } from "@/services/certificateRequestsService";
import type { CertificateRequestListItem } from "@/services/certificateRequestsService";
import { NotarialCertificateSection } from "@/pages/onboarding/NotarialCertificateSection";
import { FiscaloCertificateConfirmModal } from "@/pages/onboarding/FiscaloCertificateConfirmModal";

const ID_DIGITAL_EMPRESA_URL =
  "https://iddigital.com.uy/es/solicitud-de-certificado/empresa/facturacion_electronica/";
const CERTIFICADOS_EMAIL = "certificados@fiscalo.uy";

const COMPANY_TYPES_REQUIRING_NOTARIAL: CertificateRequestCompanyType[] = [
  "sas",
  "sa",
  "srl",
];

export type CertificateAcquisitionChoice = "myself" | "fiscalo";

export interface CertificateAcquisitionStepProps {
  mode: "choice" | "content";
  companyType?: "unipersonal" | "sas" | "srl" | "sa" | "monotributo" | null;
  onCanAdvanceChange?: (canAdvance: boolean) => void;
  /** Used in choice mode: controlled selection and callback */
  selectedChoice?: CertificateAcquisitionChoice | null;
  onChoiceChange?: (choice: CertificateAcquisitionChoice) => void;
  /** Used in content mode: which option was chosen on the previous step */
  choice?: CertificateAcquisitionChoice;
  /** Called when Fiscalo path form is valid, with payload for create certificate request; null when invalid. */
  onCertificateRequestPayload?: (payload: CreateCertificateRequestPayload | null) => void;
  /** When provided and status is pending/in_progress, step shows progress view instead of form. */
  currentCertificateRequest?: CertificateRequestListItem | null;
  currentCertificateRequestLoading?: boolean;
  /** Advance the wizard. Choice mode uses it for "Autorizar y configurar";
   *  manual-content mode uses it for the editorial Siguiente button. */
  onContinue?: () => void | Promise<void>;
  /** Step back in the wizard. Choice mode + manual-content mode both
   *  surface a Volver button. */
  onBack?: () => void;
  /** CTA loading state — used by choice mode + manual-content mode. */
  isSaving?: boolean;
  /** Manual-content mode only: gated by stepsConfirmed. */
  nextDisabled?: boolean;
  /** When true, the trail shows a 4th "CFE anteriores" step and the
   *  step tag reports "step 03 de 04". */
  hasCfeImportNext?: boolean;
  /** Drives whether the trail surfaces a "Certificado" 4th step
   *  (scratch / regularization paths). */
  situationChoice?: SituationChoice | null;
  /** Tenant context used to populate the Fiscalo confirmation modal
   *  (RUT, razón social, default trade name / phone). */
  tenantSnapshot?: {
    rut?: string | null;
    legalName?: string | null;
    tradeName?: string | null;
    phone?: string | null;
  } | null;
  /** Submit the Fiscalo cert request from the confirmation modal.
   *  Resolves with the automation's final status — the caller
   *  (useOnboarding.handleConfirmFiscaloCertificate) awaits the polling
   *  cycle so the modal sees the real outcome rather than closing
   *  before the automation reported anything. */
  onSubmitFiscaloRequest?: (
    phone: string | undefined,
    tradeName: string | undefined,
  ) => Promise<"submitted" | "failed">;
}

export function CertificateAcquisitionStep({
  mode,
  companyType = null,
  onCanAdvanceChange,
  selectedChoice = null,
  onChoiceChange,
  choice: contentChoice,
  onCertificateRequestPayload,
  currentCertificateRequest = null,
  currentCertificateRequestLoading = false,
  onContinue,
  onBack,
  isSaving = false,
  hasCfeImportNext = false,
  situationChoice = null,
  tenantSnapshot = null,
  onSubmitFiscaloRequest,
}: CertificateAcquisitionStepProps): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [stepsConfirmed, setStepsConfirmed] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [fiscaloFailureMode, setFiscaloFailureMode] = useState<
    "failed" | null
  >(null);
  const [firstName, setFirstName] = useState("");
  const [secondName, setSecondName] = useState("");
  const [firstLastName, setFirstLastName] = useState("");
  const [secondLastName, setSecondLastName] = useState("");
  const [durationYears, setDurationYears] = useState<1 | 2>(1);
  const fiscaloNamesInitializedRef = useRef(false);

  const choice = mode === "choice" ? selectedChoice ?? null : contentChoice ?? null;

  const isUnipersonalOrMonotributo =
    companyType === "unipersonal" || companyType === "monotributo";

  const showNotarialBox =
    companyType != null &&
    COMPANY_TYPES_REQUIRING_NOTARIAL.includes(
      companyType as CertificateRequestCompanyType
    );

  const option2FormValid =
    firstName.trim() !== "" &&
    firstLastName.trim() !== "" &&
    (durationYears === 1 || durationYears === 2);

  const fiscaloNotarialComplete =
    choice === "fiscalo" ? true : !showNotarialBox;
  const option2CanAdvance = option2FormValid && fiscaloNotarialComplete;

  const canAdvance =
    choice === "myself"
      ? stepsConfirmed
      : choice === "fiscalo"
        ? option2CanAdvance
        : false;

  useEffect(() => {
    onCanAdvanceChange?.(canAdvance);
  }, [canAdvance, onCanAdvanceChange]);

  useEffect(() => {
    if (choice !== "fiscalo" || !user || fiscaloNamesInitializedRef.current) return;
    fiscaloNamesInitializedRef.current = true;
    setFirstName(user.firstName?.trim() ?? "");
    setFirstLastName(user.lastName?.trim() ?? "");
    setSecondName(user.secondName?.trim() ?? "");
    setSecondLastName(user.secondLastName?.trim() ?? "");
  }, [choice, user]);

  useEffect(() => {
    if (choice !== "fiscalo" || !onCertificateRequestPayload) return;
    if (!option2FormValid || !fiscaloNotarialComplete || !companyType) {
      onCertificateRequestPayload(null);
      return;
    }
    const payload: CreateCertificateRequestPayload = {
      firstName: firstName.trim(),
      firstLastName: firstLastName.trim(),
      durationYears,
      companyType,
    };
    if (secondName.trim()) payload.secondName = secondName.trim();
    if (secondLastName.trim()) payload.secondLastName = secondLastName.trim();
    onCertificateRequestPayload(payload);
  }, [
    choice,
    onCertificateRequestPayload,
    option2FormValid,
    fiscaloNotarialComplete,
    companyType,
    firstName,
    secondName,
    firstLastName,
    secondLastName,
    durationYears,
  ]);

  const FISCALO_ICON_SRC = "/icons/fiscalo-iso-icon.svg";

  const options: {
    value: "myself" | "fiscalo";
    icon?: typeof FileCheck;
    iconSrc?: string;
    titleKey: string;
    descKey: string;
  }[] = [
    {
      value: "myself",
      icon: FileCheck,
      titleKey: "onboarding.certificateAcquisition.optionMyself",
      descKey: "onboarding.certificateAcquisition.optionMyselfDescription",
    },
    {
      value: "fiscalo",
      iconSrc: FISCALO_ICON_SRC,
      titleKey: "onboarding.certificateAcquisition.optionFiscalo",
      descKey: "onboarding.certificateAcquisition.optionFiscaloDescription",
    },
  ];

  if (mode === "choice") {
    const isCertBeforeDgi =
      situationChoice === "scratch" ||
      situationChoice === "issuer_need_regularization";
    /** DGI comes before the certificate: scratch and already_issuer. */
    const isDgiFirst =
      situationChoice === "scratch" || situationChoice === "already_issuer";
    /** Row count of the trail rendered beside this screen — keep them equal. */
    const dgiConfigTrailTotal =
      hasCfeImportNext && situationChoice === "already_issuer"
        ? 5
        : isCertBeforeDgi || isDgiFirst || hasCfeImportNext
          ? 4
          : 3;
    // ALWAYS the certificate headline. This screen asks one question — how the
    // tenant will obtain their certificate — so it says so, whatever their
    // situation.
    //
    // It used to branch: scratch and regularization got the certificate
    // wording, everyone else got "Configurá … como tu proveedor de e-Factura",
    // which describes the DGI step. That was harmless while only scratch and
    // regularization reached this screen. Once already_issuer was routed here
    // too (spec cc0cd3d9) it became actively misleading: the DGI step uses the
    // same two option labels — "Lo hacemos nosotros" / "Lo hago yo" — so with a
    // DGI-sounding headline on top, someone who had just finished DGI
    // configuration read this as the same screen served twice and reported it
    // as a bug. It is not the same screen, and it should not look like it.
    const headline = t(
      "onboarding.certificateAcquisition.modeH1Cert",
      "Tu certificado digital. ¿Cómo lo conseguimos?",
    );
    const ctaDisabled = !selectedChoice || isSaving;
    const ctaCopy =
      selectedChoice === "myself"
        ? t(
            "onboarding.certificateAcquisition.modeCtaManual",
            "Empezar manualmente",
          )
        : t(
            "onboarding.certificateAcquisition.modeCtaAuto",
            "Autorizar y configurar",
          );

    return (
      <OnboardingShell
        form={
          <>
            <OnboardingBrandMark />
            <OnboardingStepTag
              // already_issuer is DGI-first like scratch, so "Modo" is row 3 of
              // 4 for both. It used to be 2 of 3 here, which disagreed with the
              // trail beside it the moment already_issuer started reaching this
              // screen: the tag said step 2 while the trail highlighted a
              // four-row path. Two numbering systems for one screen, and they
              // have to agree.
              step={isDgiFirst ? 3 : 2}
              total={dgiConfigTrailTotal}
              label={t(
                "onboarding.certificateAcquisition.modeTag",
                "Modo",
              )}
              flow={t(
                "onboarding.certificateAcquisition.flow",
                "Certificado digital",
              )}
            />
            <h1 className="fx-onb-h1">{headline}</h1>
            <p className="fx-onb-lead">
              {t(
                "onboarding.certificateAcquisition.modeLead",
                "Para emitir tus comprobantes, Fiscalo necesita un certificado digital. ¿Cómo querés conseguirlo?",
              )}
            </p>

            <div className="fx-onb-choice-grid">
              <OnboardingChoiceCard
                icon={<Sparkles aria-hidden />}
                name={
                  <>
                    {t(
                      "onboarding.certificateAcquisition.autoNameLead",
                      "Lo hacemos",
                    )}{" "}
                    <span className="ital">
                      {t(
                        "onboarding.certificateAcquisition.autoNameAccent",
                        "nosotros.",
                      )}
                    </span>
                  </>
                }
                desc={t(
                  "onboarding.certificateAcquisition.autoDesc",
                  "Fiscalo gestiona el certificado y deja todo listo. Vos solo autorizás el proceso una vez.",
                )}
                meta={
                  <span className="meta-outcome">
                    {t(
                      "onboarding.certificateAcquisition.autoOutcome",
                      "Sin abrir DGI",
                    )}
                  </span>
                }
                ribbon={t(
                  "onboarding.certificateAcquisition.recommended",
                  "Recomendado",
                )}
                selected={selectedChoice === "fiscalo"}
                onClick={() => onChoiceChange?.("fiscalo")}
              />
              <OnboardingChoiceCard
                icon={<Settings aria-hidden />}
                name={
                  <>
                    {t(
                      "onboarding.certificateAcquisition.manualNameLead",
                      "Lo hago",
                    )}{" "}
                    <span className="ital">
                      {t(
                        "onboarding.certificateAcquisition.manualNameAccent",
                        "yo.",
                      )}
                    </span>
                  </>
                }
                desc={t(
                  "onboarding.certificateAcquisition.manualDesc",
                  "Te damos los pasos exactos para sacar tu certificado en ID Digital y subirlo a Fiscalo.",
                )}
                meta={
                  <span className="meta-outcome">
                    {t(
                      "onboarding.certificateAcquisition.manualOutcome",
                      "Vos en ID Digital, paso a paso",
                    )}
                  </span>
                }
                selected={selectedChoice === "myself"}
                onClick={() => onChoiceChange?.("myself")}
              />
            </div>

            {selectedChoice === "fiscalo" ? (
              <OnboardingAutoFlowPanel
                head={t(
                  "onboarding.certificateAcquisition.autoFlowHead",
                  "Esto es lo que Fiscalo hace por vos",
                )}
                items={[
                  {
                    label: t(
                      "onboarding.certificateAcquisition.autoFlowItem1",
                      "Genera la solicitud de certificado digital con tus datos",
                    ),
                    badge: t(
                      "onboarding.certificateAcquisition.autoFlowBadge",
                      "Automático",
                    ),
                  },
                  {
                    label: t(
                      "onboarding.certificateAcquisition.autoFlowItem2",
                      "Coordina la entrega con ID Digital · Abitab",
                    ),
                    badge: t(
                      "onboarding.certificateAcquisition.autoFlowBadge",
                      "Automático",
                    ),
                  },
                  {
                    label: t(
                      "onboarding.certificateAcquisition.autoFlowItem3",
                      "Valida la conexión emisor ↔ Fiscalo",
                    ),
                    badge: t(
                      "onboarding.certificateAcquisition.autoFlowBadge",
                      "Automático",
                    ),
                  },
                ]}
              />
            ) : null}

            <div className="fx-onb-actions">
              <div className="side">
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-secondary"
                  onClick={onBack}
                  disabled={isSaving}
                >
                  <ArrowLeft aria-hidden />
                  {t("onboarding.back", "Volver")}
                </button>
              </div>
              <div className="side end">
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-accent"
                  disabled={ctaDisabled}
                  onClick={() => {
                    if (ctaDisabled) return;
                    void onContinue?.();
                  }}
                >
                  {isSaving ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <>
                      {ctaCopy}
                      <ArrowRight aria-hidden />
                    </>
                  )}
                </button>
              </div>
            </div>
          </>
        }
        brand={
          <OnboardingBrandColumn
            eyebrow={t(
              "onboarding.certificateAcquisition.brandEyebrow",
              "Recomendamos automático",
            )}
            quote={
              <>
                {t(
                  "onboarding.certificateAcquisition.brandQuoteLead",
                  "El 90% elige automático.",
                )}{" "}
                <span className="accent">
                  {t(
                    "onboarding.certificateAcquisition.brandQuoteAccent",
                    "Nosotros lo gestionamos por vos.",
                  )}
                </span>
              </>
            }
            currentStep={situationChoice === "scratch" ? 3 : 2}
            trailSteps={getDgiConfigTrail({
              situationChoice,
              isMigratingCfes: hasCfeImportNext,
            })}
            valueBlock={{
              head: t(
                "onboarding.certificateAcquisition.brandValueHead",
                "Si elegís automático",
              ),
              items: [
                t(
                  "onboarding.certificateAcquisition.brandValueItemCert1",
                  "Pedimos tu certificado en ID Digital con tus datos",
                ),
                t(
                  "onboarding.certificateAcquisition.brandValueItemCert2",
                  "Coordinamos la visita a Abitab",
                ),
                t(
                  "onboarding.certificateAcquisition.brandValueItemCert3",
                  "Subimos el certificado a Fiscalo cuando esté listo",
                ),
                t(
                  "onboarding.certificateAcquisition.brandValueItemCert4",
                  "Vos no abrís DGI ni ID Digital",
                ),
              ],
            }}
          />
        }
      />
    );
  }

  if (choice === null) {
    return <></>;
  }

  // Editorial render for the manual path. The Fiscalo content path
  // keeps the legacy layout for now — it has its own form + progress
  // states that need a separate pass.
  if (choice === "myself") {
    const isCertBeforeDgi =
      situationChoice === "scratch" ||
      situationChoice === "issuer_need_regularization";
    // "Certificado" row: DGI-first situations (scratch, already_issuer) put it
    // at row 4; cert-first regularization at row 3.
    const isDgiFirst =
      situationChoice === "scratch" || situationChoice === "already_issuer";
    const certTrailStep = isDgiFirst ? 4 : 3;
    const stepTagTotal =
      hasCfeImportNext && situationChoice === "already_issuer"
        ? 5
        : isCertBeforeDgi || isDgiFirst || hasCfeImportNext
          ? 4
          : 3;
    const step2Copy =
      companyType === "sas" ||
      companyType === "srl" ||
      companyType === "sa"
        ? t("onboarding.certificateAcquisition.step2Sociedades", {
            type: t(
              `onboarding.certificateAcquisition.companyType.${companyType}`,
            ),
          })
        : t("onboarding.certificateAcquisition.step2", {
            type: companyType
              ? t(
                  `onboarding.certificateAcquisition.companyType.${companyType}`,
                )
              : t("onboarding.certificateAcquisition.step2FallbackType"),
          });

    return (
      <OnboardingShell
        form={
          <>
            <OnboardingBrandMark />
            <OnboardingStepTag
              step={certTrailStep}
              total={stepTagTotal}
              label={t(
                "onboarding.certificateAcquisition.manualTag",
                "Certificado",
              )}
              flow={t(
                "onboarding.certificateAcquisition.flow",
                "Certificado digital",
              )}
            />
            <h1 className="fx-onb-h1">
              {t(
                "onboarding.certificateAcquisition.manualH1Lead",
                "Tu certificado digital,",
              )}{" "}
              <span className="ital">
                {t(
                  "onboarding.certificateAcquisition.manualH1Accent",
                  "paso a paso.",
                )}
              </span>
            </h1>
            <p className="fx-onb-lead">
              {t(
                "onboarding.certificateAcquisition.manualLead",
                "Sacás tu certificado en ID Digital con estos cuatro pasos. Cuando lo tengas, lo subís acá y Fiscalo queda listo para firmar tus CFE.",
              )}
            </p>

            {showNotarialBox && (
              <div style={{ marginTop: 16 }}>
                <NotarialCertificateSection idPrefix="acquisition-myself" />
              </div>
            )}

            <div
              style={{
                marginTop: 16,
                border: "1px solid var(--fx-ink-200)",
                borderRadius: "var(--fx-r-md, 12px)",
                background: "var(--fx-s-raised-hover)",
                padding: "20px 22px",
              }}
            >
              <div
                style={{
                  fontFamily: "var(--fx-ff-mono)",
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: "0.14em",
                  color: "var(--fx-ink-500, #6B7280)",
                  marginBottom: 14,
                }}
              >
                {t("onboarding.certificateAcquisition.instructionsTitle")}
              </div>
              <ol
                style={{
                  listStyle: "none",
                  margin: 0,
                  padding: 0,
                  display: "grid",
                  gap: 14,
                }}
              >
                {[
                  <>
                    {t("onboarding.certificateAcquisition.step1")}{" "}
                    <a
                      href={ID_DIGITAL_EMPRESA_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        color: "var(--fx-accent-500)",
                        textDecoration: "underline",
                        textUnderlineOffset: 3,
                        fontWeight: 500,
                      }}
                    >
                      ID Digital
                      <ExternalLink
                        aria-hidden
                        style={{ width: 12, height: 12 }}
                      />
                    </a>
                  </>,
                  step2Copy,
                  t("onboarding.certificateAcquisition.step3"),
                  t("onboarding.certificateAcquisition.step4"),
                ].map((node, idx) => (
                  <li
                    key={idx}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 12,
                    }}
                  >
                    <span
                      aria-hidden
                      style={{
                        flexShrink: 0,
                        width: 26,
                        height: 26,
                        borderRadius: 999,
                        background:
                          "color-mix(in srgb, var(--fx-accent-300) 25%, transparent)",
                        color: "var(--fx-accent-500)",
                        display: "grid",
                        placeItems: "center",
                        fontFamily: "var(--fx-ff-mono)",
                        fontSize: 12,
                        fontWeight: 600,
                        marginTop: 1,
                      }}
                    >
                      {idx + 1}
                    </span>
                    <span
                      style={{
                        fontSize: 14,
                        lineHeight: 1.55,
                        color: "var(--fx-s-text)",
                        paddingTop: 2,
                      }}
                    >
                      {node}
                    </span>
                  </li>
                ))}
              </ol>
            </div>

            <label
              htmlFor="cert-steps-done"
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
                marginTop: 16,
                padding: 14,
                border: "1px solid var(--fx-ink-200)",
                borderRadius: "var(--fx-r-md, 12px)",
                background: "var(--fx-s-overlay)",
                cursor: "pointer",
              }}
            >
              <Checkbox
                id="cert-steps-done"
                checked={stepsConfirmed}
                onCheckedChange={(checked) => setStepsConfirmed(checked === true)}
                style={{ marginTop: 3 }}
              />
              <span
                style={{
                  fontSize: 14,
                  lineHeight: 1.5,
                  color: "var(--fx-s-text)",
                }}
              >
                {t("onboarding.certificateAcquisition.completionCheckbox")}
              </span>
            </label>

            <div className="fx-onb-actions">
              <div className="side">
                {onBack ? (
                  <button
                    type="button"
                    className="fx-onb-btn fx-onb-btn-secondary"
                    onClick={onBack}
                    disabled={isSaving}
                  >
                    <ArrowLeft aria-hidden />
                    {t("onboarding.back", "Volver")}
                  </button>
                ) : null}
              </div>
              <div className="side end">
                {onContinue ? (
                  <button
                    type="button"
                    className="fx-onb-btn fx-onb-btn-accent"
                    disabled={!stepsConfirmed || isSaving}
                    onClick={() => {
                      if (!stepsConfirmed || isSaving) return;
                      void onContinue();
                    }}
                  >
                    {isSaving ? (
                      <Loader2 className="animate-spin" />
                    ) : (
                      <>
                        {t("onboarding.next", "Siguiente")}
                        <ArrowRight aria-hidden />
                      </>
                    )}
                  </button>
                ) : null}
              </div>
            </div>
          </>
        }
        brand={
          <OnboardingBrandColumn
            eyebrow={t(
              "onboarding.certificateAcquisition.manualBrandEyebrow",
              "Tu certificado digital",
            )}
            quote={
              <>
                {t(
                  "onboarding.certificateAcquisition.manualBrandQuoteLead",
                  "La llave criptográfica que nos permite",
                )}{" "}
                <span className="accent">
                  {t(
                    "onboarding.certificateAcquisition.manualBrandQuoteAccent",
                    "firmar tus CFE en nombre de tu empresa.",
                  )}
                </span>
              </>
            }
            currentStep={certTrailStep}
            trailSteps={getDgiConfigTrail({
              situationChoice,
              isMigratingCfes: hasCfeImportNext,
            })}
            valueBlock={{
              head: t(
                "onboarding.certificateAcquisition.manualBrandValueHead",
                "Cuando termines en ID Digital",
              ),
              items: [
                t(
                  "onboarding.certificateAcquisition.manualBrandValueItem1",
                  "Lo subís acá en el siguiente paso",
                ),
                t(
                  "onboarding.certificateAcquisition.manualBrandValueItem2",
                  "Fiscalo lo guarda en HSM dedicado",
                ),
                t(
                  "onboarding.certificateAcquisition.manualBrandValueItem3",
                  "Empezás a emitir CFE en minutos",
                ),
              ],
            }}
          />
        }
      />
    );
  }

  // Fiscalo (automatic) content path — editorial layout.
  const fiscaloIsCertBeforeDgi =
    situationChoice === "scratch" ||
    situationChoice === "issuer_need_regularization";
  // "Certificado" row: scratch (DGI-first) is row 4; cert-first regularization
  // and already_issuer are row 3.
  const fiscaloTrailStep = situationChoice === "scratch" ? 4 : 3;
  const fiscaloStepTagTotal =
    fiscaloIsCertBeforeDgi || hasCfeImportNext ? 4 : 3;
  return (
    <OnboardingShell
      form={
        <>
          <OnboardingBrandMark />
          <OnboardingStepTag
            step={fiscaloTrailStep}
            total={fiscaloStepTagTotal}
            label={t(
              "onboarding.certificateAcquisition.fiscaloTag",
              "Certificado",
            )}
            flow={t(
              "onboarding.certificateAcquisition.flow",
              "Certificado digital",
            )}
          />
          <h1 className="fx-onb-h1">
            {t(
              "onboarding.certificateAcquisition.fiscaloH1Lead",
              "Confirmá los datos",
            )}{" "}
            <span className="ital">
              {t(
                "onboarding.certificateAcquisition.fiscaloH1Accent",
                "y nosotros nos encargamos.",
              )}
            </span>
          </h1>
          <p className="fx-onb-lead">
            {t(
              "onboarding.certificateAcquisition.fiscaloStepIntro",
              "Pedimos el certificado en ID Digital con estos datos. Vos solo coordinás la visita a Abitab cuando ID Digital te avise — Fiscalo hace el resto.",
            )}
          </p>

          {currentCertificateRequestLoading ? (
            <div
              style={{
                marginTop: 16,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "40px 20px",
                border: "1px solid var(--fx-ink-200)",
                borderRadius: "var(--fx-r-md, 12px)",
                background: "var(--fx-s-raised-hover)",
              }}
            >
              <Loader2
                className="animate-spin"
                aria-hidden
                style={{
                  width: 28,
                  height: 28,
                  color: "var(--fx-ink-500)",
                }}
              />
            </div>
          ) : currentCertificateRequest?.status === "pending" ||
            currentCertificateRequest?.status === "in_progress" ? (
            <div
              style={{
                marginTop: 16,
                border: "1px solid var(--fx-ink-200)",
                borderRadius: "var(--fx-r-md, 12px)",
                background: "var(--fx-s-raised-hover)",
                padding: "18px 20px",
              }}
            >
              <div
                style={{
                  fontFamily: "var(--fx-ff-mono)",
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: "0.14em",
                  color: "var(--fx-ink-500, #6B7280)",
                  marginBottom: 8,
                }}
              >
                {t("onboarding.certificateAcquisition.progressTitle")}
              </div>
              <p
                style={{
                  margin: 0,
                  fontSize: 14,
                  lineHeight: 1.55,
                  color: "var(--fx-s-text)",
                }}
              >
                {t(
                  "onboarding.certificateAcquisition.progressAwaitingAbitab",
                )}
              </p>
            </div>
          ) : (
            <>
              {showNotarialBox && (
                <div style={{ marginTop: 16 }}>
                  <NotarialCertificateSection idPrefix="acquisition-fiscalo" />
                </div>
              )}

              {/* Solicitante card */}
              <div
                style={{
                  marginTop: 16,
                  border: "1px solid var(--fx-ink-200)",
                  borderRadius: "var(--fx-r-md, 12px)",
                  background: "var(--fx-s-raised-hover)",
                  padding: "18px 20px",
                }}
              >
                <div
                  style={{
                    fontFamily: "var(--fx-ff-mono)",
                    fontSize: 11,
                    textTransform: "uppercase",
                    letterSpacing: "0.14em",
                    color: "var(--fx-ink-500, #6B7280)",
                    marginBottom: 10,
                  }}
                >
                  {t(
                    "onboarding.certificateAcquisition.solicitantLabel",
                  )}
                </div>
                {firstName.trim() ||
                firstLastName.trim() ||
                user?.displayName ? (
                  <p
                    style={{
                      margin: 0,
                      fontFamily: "var(--fx-ff-serif)",
                      fontSize: 18,
                      color: "var(--fx-s-text)",
                    }}
                  >
                    {[firstName, secondName, firstLastName, secondLastName]
                      .filter(Boolean)
                      .join(" ") ||
                      user?.displayName ||
                      user?.email}
                  </p>
                ) : (
                  <div
                    style={{
                      display: "grid",
                      gap: 12,
                      gridTemplateColumns: "1fr 1fr",
                    }}
                  >
                    <div className="fx-onb-field">
                      <label htmlFor="cert-first-name">
                        {t(
                          "onboarding.certificateAcquisition.firstName",
                        )}{" "}
                        *
                      </label>
                      <input
                        id="cert-first-name"
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder={t(
                          "onboarding.certificateAcquisition.firstNamePlaceholder",
                        )}
                      />
                    </div>
                    <div className="fx-onb-field">
                      <label htmlFor="cert-first-last">
                        {t(
                          "onboarding.certificateAcquisition.firstLastName",
                        )}{" "}
                        *
                      </label>
                      <input
                        id="cert-first-last"
                        type="text"
                        value={firstLastName}
                        onChange={(e) => setFirstLastName(e.target.value)}
                        placeholder={t(
                          "onboarding.certificateAcquisition.firstLastNamePlaceholder",
                        )}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Vigencia cards */}
              <div style={{ marginTop: 16 }}>
                <div
                  style={{
                    fontFamily: "var(--fx-ff-mono)",
                    fontSize: 11,
                    textTransform: "uppercase",
                    letterSpacing: "0.14em",
                    color: "var(--fx-ink-500, #6B7280)",
                    marginBottom: 10,
                  }}
                >
                  {t(
                    "onboarding.certificateAcquisition.chooseValidity",
                  )}
                </div>
                <div
                  style={{
                    display: "grid",
                    gap: 12,
                    gridTemplateColumns: "1fr 1fr",
                  }}
                >
                  {([1, 2] as const).map((years) => {
                    const selected = durationYears === years;
                    const priceKey = isUnipersonalOrMonotributo
                      ? `priceUnipersonal${years}`
                      : `priceOther${years}`;
                    const discountKey =
                      isUnipersonalOrMonotributo &&
                      `discountUnipersonal${years}`;
                    return (
                      <button
                        key={years}
                        type="button"
                        onClick={() => setDurationYears(years)}
                        style={{
                          textAlign: "left",
                          padding: 0,
                          background: selected
                            ? "color-mix(in srgb, var(--fx-accent-300) 12%, var(--fx-paper))"
                            : "var(--fx-paper)",
                          border: selected
                            ? "1px solid var(--fx-accent-500)"
                            : "1px solid var(--fx-ink-200)",
                          borderRadius: "var(--fx-r-md, 12px)",
                          cursor: "pointer",
                          transition:
                            "background 120ms ease, border-color 120ms ease",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            padding: "12px 16px",
                            background: selected
                              ? "var(--fx-accent-500)"
                              : "var(--fx-ink-900)",
                            // Cream text on the accent/ink fill set just
                            // above — a self-consistent dark header in both
                            // themes, not a themed surface.
                            // eslint-disable-next-line no-restricted-syntax
                            color: "var(--fx-paper)",
                            fontFamily: "var(--fx-ff-serif)",
                            fontSize: 17,
                            fontWeight: 500,
                          }}
                        >
                          {years === 1
                            ? t(
                                "onboarding.certificateAcquisition.duration1Year",
                              )
                            : t(
                                "onboarding.certificateAcquisition.duration2Years",
                              )}
                        </div>
                        <div
                          style={{
                            padding: "14px 16px",
                            display: "grid",
                            gap: 4,
                          }}
                        >
                          <p
                            style={{
                              margin: 0,
                              fontSize: 12,
                              color: "var(--fx-ink-500)",
                            }}
                          >
                            {t(
                              "onboarding.certificateAcquisition.certificateValidityYears",
                              {
                                years:
                                  years === 1
                                    ? t(
                                        "onboarding.certificateAcquisition.duration1Year",
                                      )
                                    : t(
                                        "onboarding.certificateAcquisition.duration2Years",
                                      ),
                              },
                            )}
                          </p>
                          <p
                            style={{
                              margin: 0,
                              fontSize: 14,
                              fontWeight: 600,
                              color: "var(--fx-s-text)",
                            }}
                          >
                            {t(
                              "onboarding.certificateAcquisition.priceLabel",
                            )}
                            :{" "}
                            {t(
                              `onboarding.certificateAcquisition.${priceKey}`,
                            )}
                          </p>
                          {isUnipersonalOrMonotributo && discountKey ? (
                            <p
                              style={{
                                margin: 0,
                                fontSize: 12,
                                color: "var(--fx-ink-500)",
                              }}
                            >
                              {t(
                                `onboarding.certificateAcquisition.${discountKey}`,
                              )}
                            </p>
                          ) : null}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          <div className="fx-onb-actions">
            <div className="side">
              {onBack ? (
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-secondary"
                  onClick={onBack}
                  disabled={isSaving || confirming}
                >
                  <ArrowLeft aria-hidden />
                  {t("onboarding.back", "Volver")}
                </button>
              ) : null}
            </div>
            <div className="side end">
              {onContinue ? (
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-accent"
                  disabled={
                    isSaving ||
                    confirming ||
                    (!canAdvance &&
                      currentCertificateRequest?.status !== "pending" &&
                      currentCertificateRequest?.status !== "in_progress")
                  }
                  onClick={() => {
                    if (isSaving || confirming) return;
                    // If the cert request was already created earlier
                    // (status: pending/in_progress), Siguiente moves on
                    // — no need to re-confirm. Otherwise open the
                    // confirmation modal so the user reviews data before
                    // we POST anything to ID Digital.
                    if (
                      currentCertificateRequest?.status === "pending" ||
                      currentCertificateRequest?.status === "in_progress"
                    ) {
                      void onContinue();
                      return;
                    }
                    if (onSubmitFiscaloRequest) {
                      setConfirmModalOpen(true);
                    } else {
                      void onContinue();
                    }
                  }}
                >
                  {isSaving ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <>
                      {t("onboarding.next", "Siguiente")}
                      <ArrowRight aria-hidden />
                    </>
                  )}
                </button>
              ) : null}
            </div>
          </div>

          {onSubmitFiscaloRequest && tenantSnapshot && choice === "fiscalo" ? (
            <FiscaloCertificateConfirmModal
              open={confirmModalOpen}
              onOpenChange={(o) => {
                if (!confirming) {
                  setConfirmModalOpen(o);
                  if (!o) setFiscaloFailureMode(null);
                }
              }}
              certificateRequestPayload={{
                firstName: firstName.trim(),
                firstLastName: firstLastName.trim(),
                durationYears,
                companyType: companyType ?? "unipersonal",
                ...(secondName.trim()
                  ? { secondName: secondName.trim() }
                  : {}),
                ...(secondLastName.trim()
                  ? { secondLastName: secondLastName.trim() }
                  : {}),
              }}
              documentNumber={user?.documentNumber ?? null}
              documentType={user?.documentType ?? null}
              rut={tenantSnapshot.rut ?? null}
              legalName={tenantSnapshot.legalName ?? null}
              tradeName={tenantSnapshot.tradeName ?? null}
              defaultPhone={tenantSnapshot.phone ?? null}
              confirming={confirming}
              failureMode={fiscaloFailureMode}
              onRetry={() => setFiscaloFailureMode(null)}
              onConfirm={async (phone, tradeName) => {
                setConfirming(true);
                setFiscaloFailureMode(null);
                try {
                  const status = await onSubmitFiscaloRequest(
                    phone,
                    tradeName,
                  );
                  if (status === "failed") {
                    setFiscaloFailureMode("failed");
                    return status;
                  }
                  // Fire-and-forget: cert request was created server
                  // side. The Abitab automation may still be running
                  // (or unreachable) but that's tracked elsewhere — the
                  // dashboard's onboarding card surfaces progress. Close
                  // and advance the wizard.
                  setConfirmModalOpen(false);
                  if (onContinue) await onContinue();
                  return status;
                } finally {
                  setConfirming(false);
                }
              }}
            />
          ) : null}
        </>
      }
      brand={
        <OnboardingBrandColumn
          eyebrow={t(
            "onboarding.certificateAcquisition.fiscaloBrandEyebrow",
            "Fiscalo se encarga",
          )}
          quote={
            <>
              {t(
                "onboarding.certificateAcquisition.fiscaloBrandQuoteLead",
                "Confirmás los datos,",
              )}{" "}
              <span className="accent">
                {t(
                  "onboarding.certificateAcquisition.fiscaloBrandQuoteAccent",
                  "y empieza el trámite por vos.",
                )}
              </span>
            </>
          }
          currentStep={fiscaloTrailStep}
          trailSteps={getDgiConfigTrail({
            situationChoice,
            isMigratingCfes: hasCfeImportNext,
          })}
          valueBlock={{
            head: t(
              "onboarding.certificateAcquisition.fiscaloBrandValueHead",
              "Mientras tanto",
            ),
            items: [
              t(
                "onboarding.certificateAcquisition.fiscaloBrandValueItem1",
                "Generamos la solicitud en ID Digital",
              ),
              t(
                "onboarding.certificateAcquisition.fiscaloBrandValueItem2",
                "Te llega un email con la cita en Abitab",
              ),
              t(
                "onboarding.certificateAcquisition.fiscaloBrandValueItem3",
                "Configurás Fiscalo en paralelo — no perdés tiempo",
              ),
            ],
          }}
        />
      }
    />
  );
}
